import { enterpriseSlugSchema } from "@mg/schemas/src/christo/enterpriseSlug";

/** Suggest a display name for a duplicated enterprise. */
export function suggestDuplicateEnterpriseName(sourceName: string): string {
  const trimmed = sourceName.trim();
  if (!trimmed) return "Copy";
  if (/\s2$/i.test(trimmed)) {
    return `${trimmed} copy`;
  }
  return `${trimmed} 2`;
}

/** Same rule as christo `enterpriseSlugSchema`, including the 80-character cap. */
export function isValidEnterpriseSlug(slug: string): boolean {
  return enterpriseSlugSchema.safeParse(slug).success;
}

/** Mongo collation strength 2 ignores case and keeps diacritics. */
function identityNameKey(name: string): string {
  return name.trim().toLowerCase();
}

function isSameEnterprise(id: string, excludeId: string | undefined): boolean {
  if (excludeId == null || excludeId === "") return false;
  return id.toLowerCase() === excludeId.toLowerCase();
}

export type EnterpriseIdentityField = "name" | "slug";

type EnterpriseIdentityRow = {
  _id: string;
  name: string;
  slug?: string;
};

/**
 * Client-side mirror of `EnterpriseService.findIdentityConflict`. Name is
 * trimmed and compared the way Mongo collation strength 2 does (case only);
 * slug is exact. Name wins when both collide. `excludeId` is the enterprise
 * being renamed.
 */
export function findEnterpriseIdentityConflict(
  enterprises: readonly EnterpriseIdentityRow[],
  input: { name?: string; slug?: string; excludeId?: string },
): EnterpriseIdentityField | null {
  const name = input.name != null ? identityNameKey(input.name) : undefined;
  const slug = input.slug?.trim();
  if (name) {
    const nameTaken = enterprises.some(
      (enterprise) =>
        !isSameEnterprise(enterprise._id, input.excludeId) &&
        identityNameKey(enterprise.name) === name,
    );
    if (nameTaken) return "name";
  }
  if (slug) {
    const slugTaken = enterprises.some(
      (enterprise) =>
        !isSameEnterprise(enterprise._id, input.excludeId) &&
        (enterprise.slug ?? "") === slug,
    );
    if (slugTaken) return "slug";
  }
  return null;
}

export type EnterpriseSlugProblem = "length" | "charset";

/** Why a non-empty slug cannot be saved. Length wins over the character class. */
export function enterpriseSlugProblem(
  slug: string,
): EnterpriseSlugProblem | null {
  const trimmed = slug.trim();
  if (!trimmed) return null;
  if (trimmed.length > 80) return "length";
  if (!isValidEnterpriseSlug(trimmed)) return "charset";
  return null;
}

/**
 * kebab-case slug aligned with christo `makeSlug`: characters outside
 * `[a-z0-9 -]` are dropped, then spaces become hyphens.
 */
export function enterpriseNameToSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function abortReason(signal: AbortSignal): Error {
  if (signal.reason instanceof Error) return signal.reason;
  const error = new Error("Duplicate cancelled");
  error.name = "AbortError";
  return error;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortReason(signal));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortReason(signal as AbortSignal));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function pollUntilSettled<T extends { status: string }>(
  fetchStatus: () => Promise<T>,
  options?: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<T> {
  const intervalMs = options?.intervalMs ?? 5000;
  const timeoutMs = options?.timeoutMs ?? 45 * 60 * 1000;
  const signal = options?.signal;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    if (signal?.aborted) throw abortReason(signal);
    const result = await fetchStatus();
    if (signal?.aborted) throw abortReason(signal);
    if (result.status === "succeeded") {
      return result;
    }
    if (result.status === "failed") {
      throw new Error("Duplicate job failed");
    }
    await sleep(intervalMs, signal);
  }

  throw new Error("Duplicate timed out");
}
